import React, { useEffect, useState, useMemo } from "react";
import { useParams, useNavigate, useSearchParams } from "react-router-dom";
import { admAPI } from "../../lib/admApi";
import { PageHeader, Loading, StatusPill } from "../../components/Common";
import { Search, Filter, ArrowLeft } from "lucide-react";
import { toast } from "sonner";

const STAGES = [
  "application_draft", "application_submitted", "under_review",
  "assessment_scheduled", "interview_scheduled", "review_complete",
  "offer_issued", "waiting_list", "accepted", "enrolled",
  "rejected", "withdrawn",
];

export default function AdmissionsPipeline() {
  const { school } = useParams();
  const nav = useNavigate();
  const [params, setParams] = useSearchParams();
  const [items, setItems] = useState(null);
  const [q, setQ] = useState("");
  const [year, setYear] = useState("");
  const [boarding, setBoarding] = useState("");
  const [priority, setPriority] = useState("");

  const activeStage = params.get("stage") || "";

  const load = () => {
    const p = {};
    if (q) p.q = q;
    if (year) p.year = year;
    if (activeStage) p.stage = activeStage;
    if (boarding) p.boarding = boarding === "yes";
    if (priority) p.priority = priority;
    admAPI.listApplicants(p).then(({ data }) => setItems(data)).catch(() => setItems([]));
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [activeStage, q, year, boarding, priority]);

  const byStage = useMemo(() => {
    if (!items) return {};
    return items.reduce((acc, a) => {
      (acc[a.stage] = acc[a.stage] || []).push(a);
      return acc;
    }, {});
  }, [items]);

  const advance = async (applicant, stage) => {
    try {
      await admAPI.changeStage(applicant.id, stage);
      toast.success(`${applicant.first_name} moved to ${stage.replace(/_/g, " ")}`);
      load();
    } catch { toast.error("Stage change failed"); }
  };

  if (items === null) return <Loading />;

  return (
    <div data-testid="admissions-pipeline">
      <PageHeader
        kicker="Admissions360™"
        title="Applicant pipeline"
        subtitle={`${items.length} applicants tracked · Drag mental focus stage-by-stage.`}
        actions={
          <button onClick={() => nav(`/education360/${school}/admissions`)} className="hl-button-secondary text-xs" data-testid="btn-back-hub">
            <ArrowLeft className="w-3 h-3 inline mr-1" />Command Centre
          </button>
        }
      />

      {/* Filter bar */}
      <div className="hl-card p-3 mb-6 flex flex-wrap items-center gap-2" data-testid="filter-bar">
        <div className="relative flex-1 min-w-[220px]">
          <Search className="w-4 h-4 text-slate-400 absolute left-3 top-1/2 -translate-y-1/2" />
          <input
            data-testid="filter-search"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Search name, application no, country…"
            className="pl-9 pr-4 py-2 text-sm bg-slate-50 border border-slate-200 rounded w-full"
          />
        </div>
        <select value={year} onChange={(e) => setYear(e.target.value)} className="text-sm border border-slate-200 rounded px-2 py-2" data-testid="filter-year">
          <option value="">All years</option>
          {["Year 7", "Year 8", "Year 9", "Year 10", "Year 11", "Year 12", "Year 13"].map(y => <option key={y} value={y}>{y}</option>)}
        </select>
        <select value={activeStage} onChange={(e) => { const s = e.target.value; if (s) setParams({ stage: s }); else setParams({}); }} className="text-sm border border-slate-200 rounded px-2 py-2" data-testid="filter-stage">
          <option value="">All stages</option>
          {STAGES.map(s => <option key={s} value={s}>{s.replace(/_/g, " ")}</option>)}
        </select>
        <select value={boarding} onChange={(e) => setBoarding(e.target.value)} className="text-sm border border-slate-200 rounded px-2 py-2" data-testid="filter-boarding">
          <option value="">Boarding: any</option>
          <option value="yes">Boarding only</option>
          <option value="no">Day only</option>
        </select>
        <select value={priority} onChange={(e) => setPriority(e.target.value)} className="text-sm border border-slate-200 rounded px-2 py-2" data-testid="filter-priority">
          <option value="">Priority: any</option>
          <option value="sibling">Sibling</option>
          <option value="scholarship">Scholarship</option>
          <option value="alumni_family">Alumni family</option>
          <option value="standard">Standard</option>
        </select>
      </div>

      {/* Kanban board */}
      <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-4" data-testid="kanban-board">
        {STAGES.map((stage) => {
          const list = byStage[stage] || [];
          return (
            <div key={stage} className="hl-card p-3" data-testid={`col-${stage}`}>
              <div className="flex items-center justify-between mb-3 px-1">
                <div className="flex items-center gap-2">
                  <StatusPill status={stage} />
                  <span className="text-xs text-slate-500">{list.length}</span>
                </div>
              </div>
              <div className="space-y-2 max-h-[520px] overflow-y-auto">
                {list.length === 0 && <div className="text-xs text-slate-400 px-1 py-2">Empty</div>}
                {list.map((a) => (
                  <div
                    key={a.id}
                    className="border border-slate-200 rounded-md p-3 hover:border-[color:var(--hl-orange)] cursor-pointer transition-colors bg-white"
                    onClick={() => nav(`/education360/${school}/admissions/applicant/${a.id}`)}
                    data-testid={`card-${a.id}`}
                  >
                    <div className="font-semibold text-sm text-slate-800">{a.first_name} {a.last_name}</div>
                    <div className="text-[11px] text-slate-500 mono">{a.application_no}</div>
                    <div className="text-xs text-slate-600 mt-1">{a.year_group_applied} · {a.nationality}</div>
                    <div className="flex items-center justify-between mt-2">
                      <span className="text-[10px] uppercase tracking-widest text-slate-400">{a.priority}</span>
                      <span className="text-[10px] mono text-[color:var(--hl-orange)]">score {a.priority_score || 0}</span>
                    </div>
                    <select
                      className="mt-2 w-full text-[11px] border border-slate-200 rounded px-1 py-1"
                      value=""
                      onClick={(e) => e.stopPropagation()}
                      onChange={(e) => advance(a, e.target.value)}
                      data-testid={`advance-${a.id}`}
                    >
                      <option value="">Move to…</option>
                      {STAGES.filter(s => s !== stage).map(s => <option key={s} value={s}>{s.replace(/_/g, " ")}</option>)}
                    </select>
                  </div>
                ))}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
